/**
 * Utility function to parse Firestore timestamps
 * Handles both Firestore timestamp objects and regular date strings/numbers
 * @param {*} timestamp - The timestamp to parse (can be Firestore timestamp, string, or number)
 * @returns {Date|null} - A JavaScript Date object or null if invalid
 */
export const parseFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // Handle Firestore timestamp format {_seconds, _nanoseconds}
  if (timestamp._seconds) {
    return new Date(timestamp._seconds * 1000);
  }
  
  // Handle regular timestamp string or number
  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  
  return null;
};

/**
 * Formats a Firestore timestamp to a locale string
 * @param {*} timestamp - The timestamp to format
 * @param {string} locales - The locale for formatting (default: undefined for system locale)
 * @param {object} options - Formatting options for toLocaleString
 * @returns {string} - Formatted date string or 'N/A' if invalid
 */
export const formatFirestoreTimestamp = (timestamp, locales = undefined, options = undefined) => {
  const date = parseFirestoreTimestamp(timestamp);
  if (!date) return 'N/A';
  
  try {
    return date.toLocaleString(locales, options);
  } catch (error) {
    console.warn('Error formatting timestamp:', error);
    return 'N/A';
  }
};

/**
 * Checks if a timestamp (Firestore or regular) is valid
 * @param {*} timestamp - The timestamp to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidTimestamp = (timestamp) => {
  if (!timestamp) return false;
  
  // Handle Firestore timestamp format
  if (typeof timestamp === 'object' && timestamp._seconds) {
    return true;
  }
  
  // Handle regular timestamp
  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const date = new Date(timestamp);
    return !isNaN(date.getTime());
  }
  
  return false;
}; 